import { getRequest, postRequest, patchRequest, deleteRequest } from "../utils/axios";

const API_BASE =
  process.env.REACT_APP_API_URL
  || (process.env.NODE_ENV === "production"
    ? "https://a.weave.md"
    : "http://localhost:8000");

const projectQuery = (projectHash, ref) => {
  const params = new URLSearchParams({ project: projectHash });
  if (ref) params.set("ref", ref);
  return `?${params.toString()}`;
};

export const tablesService = {
  list: (projectHash, { ref } = {}, onSuccess, onError) => {
    if (typeof ref === "function") {
      // Back-compat: list(hash, onSuccess, onError)
      return getRequest(`/api/v1/tables/${projectQuery(projectHash)}`, ref, onSuccess);
    }
    return getRequest(
      `/api/v1/tables/${projectQuery(projectHash, ref)}`,
      onSuccess,
      onError,
    );
  },
  diff: (projectHash, { table, page = 1, from: fromRef, to: toRef } = {}, onSuccess, onError) => {
    const params = new URLSearchParams({ project: projectHash, page: String(page) });
    if (table) params.set("table", table);
    if (fromRef) params.set("from", fromRef);
    if (toRef) params.set("to", toRef);
    return getRequest(`/api/v1/tables/diff/?${params.toString()}`, onSuccess, onError);
  },
  meta: (projectHash, onSuccess, onError) =>
    getRequest(`/api/v1/tables/meta/${projectQuery(projectHash)}`, onSuccess, onError),
  uploadCsv: (projectHash, file, onSuccess, onError) => {
    const data = new FormData();
    data.append("file", file);
    const query = projectHash ? projectQuery(projectHash) : "";
    return postRequest(`/api/v1/tables/upload/${query}`, data, onSuccess, onError);
  },
  downloadCsv: async (projectHash, tableHash, filename, { ref } = {}) => {
    const response = await fetch(
      `${API_BASE}/api/v1/tables/${tableHash}/download/${projectQuery(projectHash, ref)}`,
      { headers: { Authorization: `Bearer ${localStorage.getItem("accessToken")}` } },
    );
    if (!response.ok) throw new Error("Could not download CSV");
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename.endsWith(".csv") ? filename : `${filename}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  },
  replaceCsv: (projectHash, tableHash, file, { dryRun = false, ref } = {}, onSuccess, onError) => {
    const data = new FormData();
    data.append("file", file);
    const params = new URLSearchParams({ project: projectHash });
    if (dryRun) params.set("dry_run", "1");
    if (ref) params.set("ref", ref);
    return postRequest(
      `/api/v1/tables/${tableHash}/replace/?${params.toString()}`,
      data,
      onSuccess,
      onError,
    );
  },
  create: (projectHash, data, onSuccess, onError) => {
    const query = projectHash ? projectQuery(projectHash) : "";
    return postRequest(`/api/v1/tables/${query}`, data, onSuccess, onError);
  },
  update: (projectHash, hash, data, onSuccess, onError) =>
    patchRequest(`/api/v1/tables/${hash}/${projectQuery(projectHash)}`, data, onSuccess, onError),
  remove: (projectHash, hash, onSuccess, onError) =>
    deleteRequest(`/api/v1/tables/${hash}/${projectQuery(projectHash)}`, onSuccess, onError),
  get: (projectHash, tableHash, { ref, include } = {}, onSuccess, onError) => {
    const params = new URLSearchParams({ project: projectHash });
    if (ref) params.set("ref", ref);
    if (include) params.set("include", include);
    return getRequest(
      `/api/v1/tables/${tableHash}/?${params.toString()}`,
      onSuccess,
      onError,
    );
  },
  listRows: (projectHash, tableHash, { search = "", page = 1, ref } = {}, onSuccess, onError) => {
    const params = new URLSearchParams({ project: projectHash, page: String(page) });
    if (search) params.set("search", search);
    if (ref) params.set("ref", ref);
    return getRequest(`/api/v1/tables/${tableHash}/rows/?${params.toString()}`, onSuccess, onError);
  },
  getRow: (projectHash, tableHash, rowIndex, { ref } = {}, onSuccess, onError) => {
    if (typeof ref === "function") {
      return getRequest(
        `/api/v1/tables/${tableHash}/rows/${rowIndex}/${projectQuery(projectHash)}`,
        ref,
        onSuccess,
      );
    }
    return getRequest(
      `/api/v1/tables/${tableHash}/rows/${rowIndex}/${projectQuery(projectHash, ref)}`,
      onSuccess,
      onError,
    );
  },
  updateRow: (projectHash, tableHash, rowIndex, data, onSuccess, onError) =>
    patchRequest(
      `/api/v1/tables/${tableHash}/rows/${rowIndex}/${projectQuery(projectHash, "draft")}`,
      data,
      onSuccess,
      onError,
    ),
  bulkUpdateRows: (projectHash, tableHash, updates, onSuccess, onError) =>
    postRequest(
      `/api/v1/tables/${tableHash}/rows/bulk/${projectQuery(projectHash, "draft")}`,
      { updates },
      onSuccess,
      onError,
    ),
};
